import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import { 
  Package, Box, ShoppingBag, Award,
  Factory, Workflow, Shield,
  Recycle, LineChart, Leaf,
  MapPin, Building2, Phone, Mail,
  Instagram, Facebook, Twitter, Linkedin,
  Settings, Cpu, Zap, Target
} from "lucide-react";
import CountUp from '../components/CountUp';
import QuoteRequestForm from '../components/QuoteRequestForm';

const IndexEnglish = () => {
  useEffect(() => {
    document.documentElement.dir = "ltr";
  }, []);

  const handleScroll = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const products = [
    {
      icon: <Package className="w-12 h-12 text-blue-600" />,
      title: "PE Polythene Bags",
      description: "High-quality polyethylene bags in various specifications",
      features: ["Available in multiple sizes", "Tear-resistant", "Eco-friendly options"],
      subCategories: ["High-Density (HD)", "Low-Density (LD)", "Custom thickness"],
      sizes: ["Small: 15x20 cm", "Medium: 25x35 cm", "Large: 40x60 cm", "Extra Large: 50x80 cm"],
      colors: ["Transparent", "White", "Black", "Blue", "Red", "Green", "Yellow"]
    },
    {
      icon: <Box className="w-12 h-12 text-blue-600" />,
      title: "PP Polypropylene Products", 
      description: "Durable polypropylene containers and packaging solutions",
      features: ["Modern designs", "Highly durable", "Food-safe certified"],
      subCategories: ["BOPP films", "Woven bags", "Custom printing available"],
      sizes: ["500ml containers", "1L containers", "2L containers", "5L containers"],
      colors: ["Clear", "White", "Blue", "Green", "Custom colors available"]
    },
    {
      icon: <ShoppingBag className="w-12 h-12 text-blue-600" />,
      title: "Shopping & Retail Bags",
      description: "Premium shopping bags for stores and retail businesses",
      features: ["Custom branding", "High durability", "Premium printing quality"],
      subCategories: ["Laundry covers", "Nylon covers", "Sufra rolls"],
      sizes: ["Small: 20x25 cm", "Medium: 30x40 cm", "Large: 40x50 cm"],
      colors: ["White", "Black", "Brown", "Custom brand colors"]
    }
  ];

  const features = [
    {
      icon: <Award className="w-8 h-8 text-blue-600" />,
      title: "Quality Certifications",
      description: "ISO certified with international quality standards"
    },
    {
      icon: <Recycle className="w-8 h-8 text-blue-600" />,
      title: "Eco-Friendly",
      description: "Recyclable materials and sustainable practices"
    },
    {
      icon: <LineChart className="w-8 h-8 text-blue-600" />,
      title: "Quality Management",
      description: "Comprehensive quality assurance systems"
    },
    {
      icon: <Leaf className="w-8 h-8 text-blue-600" />,
      title: "Sustainability",
      description: "Committed to environmental responsibility"
    }
  ];

  const qualityStandards = [
    {
      icon: <Award className="w-12 h-12 text-blue-600" />,
      title: "ISO 9001:2015",
      description: "International quality management system certification ensuring consistent high-quality products and services."
    },
    {
      icon: <Shield className="w-12 h-12 text-blue-600" />,
      title: "Food Safety Standards",
      description: "FDA approved materials safe for food contact, meeting all health and safety regulations."
    },
    {
      icon: <Recycle className="w-12 h-12 text-blue-600" />,
      title: "Environmental Compliance",
      description: "Eco-friendly production processes with recyclable materials and sustainable manufacturing practices."
    },
    {
      icon: <Target className="w-12 h-12 text-blue-600" />,
      title: "Quality Testing",
      description: "Rigorous testing procedures at every production stage to ensure durability and performance standards."
    }
  ];

  const technologyFeatures = [
    {
      icon: <Settings className="w-12 h-12 text-blue-600" />,
      title: "Advanced Machinery",
      description: "State-of-the-art German and Italian machinery for precision manufacturing and consistent quality output."
    },
    {
      icon: <Cpu className="w-12 h-12 text-blue-600" />,
      title: "Automated Systems",
      description: "Fully automated production lines with real-time monitoring and quality control systems."
    },
    {
      icon: <Zap className="w-12 h-12 text-blue-600" />,
      title: "Innovation & Development",
      description: "Continuous investment in R&D for new product development and manufacturing process improvements."
    }
  ];

  const galleryItems = [
    {
      id: 1,
      category: 'products',
      title: 'PE Polythene Bags',
      image: '/lovable-uploads/c9483b7c-65a3-421f-9f7d-a239aec7df91.png'
    },
    {
      id: 2,
      category: 'factory',
      title: 'Factory Production Line',
      image: '/lovable-uploads/a363e14f-9bef-4b1f-9e69-52824a5eb919.png'
    },
    {
      id: 3,
      category: 'products',
      title: 'Various Plastic Containers',
      image: '/lovable-uploads/30868187-d326-4652-b3af-7a0c963ca9bb.png'
    },
    {
      id: 4,
      category: 'factory',
      title: 'Modern Manufacturing Equipment',
      image: '/lovable-uploads/ef0cab00-247c-42aa-80a7-f576ae6a18e5.png'
    },
    {
      id: 5,
      category: 'packaging',
      title: 'Advanced Packaging Solutions',
      image: '/lovable-uploads/be1f56f5-df17-4b8d-82e0-31afe6d9f1e4.png'
    },
    {
      id: 6,
      category: 'packaging',
      title: 'Ready-to-Ship Products',
      image: '/lovable-uploads/e2bae37f-200e-43d6-b590-4e94af4789c7.png'
    }
  ];

  const galleryCategories = [
    { id: 'all', name: 'All' },
    { id: 'products', name: 'Products' },
    { id: 'factory', name: 'Factory' },
    { id: 'packaging', name: 'Packaging & Delivery' }
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredItems = selectedCategory === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white flex flex-col w-full overflow-x-hidden">
      <Header />
      
      {/* Home Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 to-gray-50 w-full px-4 py-20 md:py-32">
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight">
              Dan Plastic Products Factory Co.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto px-4">
              Leading manufacturer of high-quality plastic products in Saudi Arabia
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8 px-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => handleScroll('products')}
                className="w-full sm:w-auto bg-[#274f88] text-white px-8 py-3 rounded-lg hover:bg-[#1e3d6a] transition-colors"
              >
                Our Products
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => handleScroll('quote')}
                className="w-full sm:w-auto border-2 border-[#274f88] text-[#274f88] px-8 py-3 rounded-lg hover:bg-[#274f88] hover:text-white transition-colors"
              >
                Request Quote
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 md:py-32 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">About Dan Plastic</h2>
            <div className="w-24 h-1.5 bg-blue-600 rounded-full mx-auto mb-10"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Since 1989, Dan Plastic Factory has been at the forefront of plastic manufacturing in Saudi Arabia, 
              producing high-quality polyethylene and polypropylene products with precision and excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Factory Area</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">10,000</p>
              <p className="text-gray-600">Square meters of modern manufacturing facility</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Factory className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Production Capacity</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">5,000</p>
              <p className="text-gray-600">Tons annually with room for expansion</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Experience</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">35+</p>
              <p className="text-gray-600">Years of excellence in plastic manufacturing</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Quality Standards</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">ISO</p>
              <p className="text-gray-600">Certified with international quality standards</p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Our Mission</h3>
                <p className="text-gray-700">To provide innovative, high-quality plastic solutions while maintaining environmental responsibility and exceeding customer expectations.</p>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Our Vision</h3>
                <p className="text-gray-700">To be the leading plastic manufacturing company in the region, known for quality, innovation, and sustainability.</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="rounded-2xl overflow-hidden shadow-2xl"
                >
                  <img 
                    src="/lovable-uploads/a363e14f-9bef-4b1f-9e69-52824a5eb919.png" 
                    alt="Dan Plastic Factory" 
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Products</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Discover our comprehensive range of high-quality plastic products designed to meet all your packaging and storage needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="h-32 md:h-40 relative overflow-hidden bg-blue-50 flex items-center justify-center">
                  {product.icon}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">{product.title}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Available Options:</h4>
                    <ul className="space-y-1 text-gray-600">
                      {product.subCategories.map((sub, idx) => (
                        <li key={idx} className="flex items-center text-sm">
                          <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          {sub}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Available Sizes:</h4>
                    <ul className="space-y-1 text-gray-600">
                      {product.sizes.map((size, idx) => (
                        <li key={idx} className="flex items-center text-sm">
                          <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          {size}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Available Colors:</h4>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {product.colors.map((color, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Features:</h4>
                    <ul className="space-y-2 text-gray-600">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm">
                          <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards Section */}
      <section id="quality" className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Quality Standards & Certifications</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Our commitment to excellence is reflected in our adherence to international quality standards and certifications</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualityStandards.map((standard, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow text-center"
              >
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  {standard.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{standard.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{standard.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing Capabilities */}
      <section id="manufacturing" className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Manufacturing & Capabilities</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">State-of-the-art machinery and technology for superior quality production</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-xl hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Factory className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3 text-center">Advanced Machinery</h3>
              <p className="text-gray-600 text-center">Latest equipment and technology for precision manufacturing</p>
              <ul className="mt-4 space-y-2 text-gray-600 text-sm">
                <li>• Daily production capacity: 15+ tons</li>
                <li>• 24/7 operational capability</li>
                <li>• Automated quality control systems</li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-6 rounded-xl hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Workflow className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3 text-center">Quality Assurance</h3>
              <p className="text-gray-600 text-center">Comprehensive quality control at every production stage</p>
              <ul className="mt-4 space-y-2 text-gray-600 text-sm">
                <li>• ISO 9001:2015 certified</li>
                <li>• Food-safe materials</li>
                <li>• Rigorous testing procedures</li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-6 rounded-xl hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Shield className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3 text-center">Safety Standards</h3>
              <p className="text-gray-600 text-center">Highest safety protocols in all manufacturing processes</p>
              <ul className="mt-4 space-y-2 text-gray-600 text-sm">
                <li>• OSHA compliance</li>
                <li>• Regular safety training</li>
                <li>• Environmental protection</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Technology & Innovation</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Leveraging cutting-edge technology and continuous innovation to deliver superior products and services</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {technologyFeatures.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  {tech.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">{tech.title}</h3>
                <p className="text-gray-600 text-center leading-relaxed">{tech.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Request Section */}
      <QuoteRequestForm language="en" />

      {/* Gallery Section */}
      <section id="gallery" className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Image Gallery</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Browse our collection of images showcasing our high-quality products and advanced factory facilities</p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {galleryCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-xl bg-gray-100 aspect-square cursor-pointer"
              >
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Why Choose Dan Plastic</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We are committed to delivering the best products and highest quality standards</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 text-center mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-center">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1">
              <img 
                src="/lovable-uploads/65a27053-2815-4aea-bbd9-bc3d0a0633d1.png" 
                alt="Dan Plastic - Logo" 
                className="h-12 w-auto mb-4"
              />
              <p className="text-left text-sm text-gray-400">
                Leading manufacturer of high-quality plastic products
                <br />
                in Saudi Arabia since 1989
              </p>
            </div>

            <div className="col-span-1">
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => handleScroll('about')}
                  className="block text-gray-400 hover:text-white transition-colors text-left"
                >
                  About Us
                </button>
                <button 
                  onClick={() => handleScroll('products')}
                  className="block text-gray-400 hover:text-white transition-colors text-left"
                >
                  Our Products
                </button>
                <button 
                  onClick={() => handleScroll('manufacturing')}
                  className="block text-gray-400 hover:text-white transition-colors text-left"
                >
                  Manufacturing
                </button>
                <button 
                  onClick={() => handleScroll('quote')}
                  className="block text-gray-400 hover:text-white transition-colors text-left"
                >
                  Request Quote
                </button>
                <button 
                  onClick={() => handleScroll('gallery')}
                  className="block text-gray-400 hover:text-white transition-colors text-left"
                >
                  Gallery
                </button>
              </div>
            </div>

            <div className="col-span-1">
              <h3 className="text-lg font-semibold mb-4">Our Products</h3>
              <div className="space-y-2">
                <p className="text-gray-400">PE Polythene Bags</p>
                <p className="text-gray-400">PP Products</p>
                <p className="text-gray-400">Shopping Bags</p>
                <p className="text-gray-400">Industrial Packaging</p>
                <p className="text-gray-400">Custom Solutions</p>
              </div>
            </div>

            <div className="col-span-1">
              <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
              <div className="space-y-2">
                <div className="flex items-center text-gray-400">
                  <MapPin className="mr-2 w-5 h-5" />
                  <span>Riyadh, Saudi Arabia</span>
                </div>
                <div className="flex items-center text-gray-400">
                  <Mail className="mr-2 w-5 h-5" />
                  <span>info@danplastic.com</span>
                </div>
                <div className="flex items-center text-gray-400">
                  <Phone className="mr-2 w-5 h-5" />
                  <a 
                    href="tel:920000109" 
                    className="hover:text-white transition-colors"
                  >
                    920000109
                  </a>
                </div>
                
                <div className="pt-4">
                  <h4 className="text-sm font-semibold mb-2">Follow Us</h4>
                  <div className="flex space-x-3">
                    <a href="https://www.instagram.com/dan.plastic" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a href="https://www.facebook.com/dan.plastic" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a href="https://x.com/DanPlastic90" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <Twitter className="w-5 h-5" />
                    </a>
                    <a href="https://www.linkedin.com/company/danplastic/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Dan Plastic Products Factory Co. All rights reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IndexEnglish;
